import { Feature, REDUCER_FEATURE_NAME, reducerFeature } from '@edikk-edikk/feature';
import { reducerNotifier } from '@edikk-edikk/notifier';
import { Container } from 'inversify';
import { HEADER_X_DOMAIN, HEADER_X_LANGUAGE, NetworkServiceInterface } from '@edikk-edikk/network-service';
import { determineApiUrl } from '@edikk-edikk/shared';
import {
  REDUCER_TRANSLATE_NAME,
  reducerTranslate,
  REDUCER_ERROR_NAME,
  reducerError,
} from '@edikk-edikk/redux';
import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { canUseDOM } from 'exenv';
import { REDUCER_SUPPORT_MODAL_NAME, reducerSupportModal } from '@edikk-edikk/country-frontend';
import { Api } from '#src/react/api/Api';
import { reducerMenu } from '#baseMenu/reducers/slice-menu';
import { REDUCER_BASE_NAME } from '#base/constants';
import { reducerBase } from '#base/reducers';
import { REDUCER_ADVERT_CONTACTS_NAME } from '#advertContacts/constants';
import { reducerAdvertContacts } from '#advertContacts/reducers';
import { reducerAdvertFavorite } from '#advertFavorite/reducers';
import { REDUCER_ADVERT_FAVORITE_NAME } from '#advertFavorite/constants';
import { reducerAdvertReviewCheck } from '#reviewCheck/reducers';
import { REDUCER_ADVERT_REVIEW_CHECK_NAME } from '#reviewCheck/constants';
import { DI_CONTAINER_TYPE } from '#srcJs/di-container';

const initStore = (
  diContainer: Container,
  domain: string,
  preloadedState = {},
) => {
  const api = diContainer.get<Api>(DI_CONTAINER_TYPE.api);
  const feature = diContainer.get<Feature>(DI_CONTAINER_TYPE.feature);
  const networkService = diContainer.get<NetworkServiceInterface>(DI_CONTAINER_TYPE.networkService);
  networkService.setAdditionalConfig({
    headers: {
      [HEADER_X_DOMAIN]: domain,
    },
  });

  const url = new URL(determineApiUrl());
  if (canUseDOM && url.host !== domain) {
    url.host = domain;

    networkService.setBaseUrl(url.toString());
  }

  const store = configureStore({
    preloadedState,
    reducer: {
      [REDUCER_ADVERT_FAVORITE_NAME]: reducerAdvertFavorite,
      [REDUCER_SUPPORT_MODAL_NAME]: reducerSupportModal,
      [REDUCER_ADVERT_REVIEW_CHECK_NAME]: reducerAdvertReviewCheck,
      [REDUCER_ADVERT_CONTACTS_NAME]: reducerAdvertContacts,
      [REDUCER_ERROR_NAME]: reducerError,
      [REDUCER_FEATURE_NAME]: reducerFeature,
      [REDUCER_TRANSLATE_NAME]: reducerTranslate,
      [REDUCER_BASE_NAME]: reducerBase,
      notifier: reducerNotifier,
      menu: reducerMenu,
      [api.getApiHome().reducerPath]: api.getApiHome().reducer,
      [api.getApiAdvertDetail().reducerPath]: api.getApiAdvertDetail().reducer,
      [api.getApiAdvertReviewCheck().reducerPath]: api.getApiAdvertReviewCheck().reducer,
      [api.getApiAdvertFavorite().reducerPath]: api.getApiAdvertFavorite().reducer,
      [api.getApiLanguageModal().reducerPath]: api.getApiLanguageModal().reducer,
      [api.getApiBase().reducerPath]: api.getApiBase().reducer,
      [api.getApiReviewList().reducerPath]: api.getApiReviewList().reducer,
      [api.getApiAdvertListByDate().reducerPath]: api.getApiAdvertListByDate().reducer,
      [api.getApiAdvertListByService().reducerPath]: api.getApiAdvertListByService().reducer,
      [api.getApiPage().reducerPath]: api.getApiPage().reducer,
      [api.getApiUserLogin().reducerPath]: api.getApiUserLogin().reducer,
      [api.getApiUserRegistration().reducerPath]: api.getApiUserRegistration().reducer,
      [api.getApiUserForgotPassword().reducerPath]: api.getApiUserForgotPassword().reducer,
      [api.getApiUserResetPassword().reducerPath]: api.getApiUserResetPassword().reducer,
    },
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['sweetAlert/show', 'sweetAlert/showError'],
        ignoredPaths: ['sweetAlert'],
      },
    })
      .concat([
        api.getApiHome().middleware,
        api.getApiAdvertDetail().middleware,
        api.getApiAdvertReviewCheck().middleware,
        api.getApiAdvertFavorite().middleware,
        api.getApiLanguageModal().middleware,
        api.getApiBase().middleware,
        api.getApiReviewList().middleware,
        api.getApiAdvertListByDate().middleware,
        api.getApiAdvertListByService().middleware,
        api.getApiPage().middleware,
        api.getApiUserLogin().middleware,
        api.getApiUserRegistration().middleware,
        api.getApiUserForgotPassword().middleware,
        api.getApiUserResetPassword().middleware,
      ]),
  });

  setupListeners(store.dispatch);

  // @ts-ignore
  feature.setList(store.getState()[REDUCER_FEATURE_NAME].featureDataList);

  if (store.getState()[REDUCER_TRANSLATE_NAME].language) {
    networkService.setAdditionalConfig({
      headers: {
        [HEADER_X_LANGUAGE]: store.getState()[REDUCER_TRANSLATE_NAME].language,
      },
    });
  }

  return store;
};

export { initStore };
